/**
 * Artifact Action Component
 * 
 * Flexible, configuration-driven action component for artifact cards.
 * Supports field updates, validation, and API calls.
 */

import React, { useState, useCallback } from 'react';
import { CardSectionProps } from '../../../types/compositeCards';
import { 
  ActionConfig, 
  FieldConfig, 
  ActionComponentState,
  ValidationRules,
  WorkflowStep
} from '../../../types/actionConfig';
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import api from '../../../services/api/api';
import { ArtifactStatusDisplay } from './ArtifactStatusDisplay';
import { WorkflowStepsDisplay } from './WorkflowStepsDisplay';

export const ArtifactActionComponent: React.FC<CardSectionProps> = ({
  section,
  card,
  onAction
}) => {
  const { data } = card;
  
  // Get action configuration and artifact from card data
  const actionConfig: ActionConfig | undefined = (data as any).actionConfig;
  const artifact = ('artifact' in data) ? data.artifact : null;
  
  // Only log in development mode and sample to reduce verbosity
  if (process.env.NODE_ENV === 'development' && Math.random() < 0.05) {
    console.log('🔍 [ArtifactActionComponent] Rendering with (sampled):', {
      hasActionConfig: !!actionConfig,
      hasArtifact: !!artifact,
      artifactId: artifact?.id?.substring(0, 30),
      artifactType: artifact?.type
    });
  }
  
  // Initialize field values from current artifact values or defaults
  const initializeFieldValues = useCallback(() => {
    const values: Record<string, any> = {};
    if (actionConfig?.fields && artifact) {
      actionConfig.fields.forEach(field => {
        const artifactData = artifact as any; // Type assertion for dynamic field access
        values[field.fieldName] = field.currentValue ?? artifactData[field.fieldName] ?? '';
      });
    }
    return values;
  }, [actionConfig, artifact]);

  const [state, setState] = useState<ActionComponentState>(() => ({
    fieldValues: initializeFieldValues(),
    fieldErrors: {},
    isSubmitting: false,
    submitSuccess: false,
    submitError: null
  }));

  // Validate a single field
  const validateField = useCallback((field: FieldConfig, value: any): string | null => {
    const rules = field.validation;
    if (!rules) return null;

    // Required validation
    if (rules.required && !value) {
      return rules.errorMessage || `${field.label} is required`;
    }

    // Text validations
    if (field.fieldType === 'text' || field.fieldType === 'textarea') {
      const strValue = String(value);
      if (rules.minLength && strValue.length < rules.minLength) {
        return rules.errorMessage || `${field.label} must be at least ${rules.minLength} characters`;
      }
      if (rules.maxLength && strValue.length > rules.maxLength) {
        return rules.errorMessage || `${field.label} must be at most ${rules.maxLength} characters`;
      }
      if (rules.pattern && !rules.pattern.test(strValue)) {
        return rules.errorMessage || `${field.label} format is invalid`;
      }
    }

    // Number validations
    if (field.fieldType === 'number') {
      const numValue = Number(value);
      if (rules.min !== undefined && numValue < rules.min) {
        return rules.errorMessage || `${field.label} must be at least ${rules.min}`;
      }
      if (rules.max !== undefined && numValue > rules.max) {
        return rules.errorMessage || `${field.label} must be at most ${rules.max}`;
      }
    }

    // Custom validation
    if (rules.custom) {
      const result = rules.custom(value);
      if (result !== true) {
        return typeof result === 'string' ? result : 'Validation failed';
      }
    }

    return null;
  }, []);

  // Handle field change
  const handleFieldChange = useCallback((fieldName: string, value: any, field: FieldConfig) => {
    setState(prev => ({
      ...prev,
      fieldValues: { ...prev.fieldValues, [fieldName]: value },
      fieldErrors: { ...prev.fieldErrors, [fieldName]: '' },
      submitSuccess: false,
      submitError: null
    }));

    // Validate on change
    const error = validateField(field, value);
    if (error) {
      setState(prev => ({
        ...prev,
        fieldErrors: { ...prev.fieldErrors, [fieldName]: error }
      }));
    }
  }, []);

  // Validate all fields
  const validateAllFields = useCallback((): boolean => {
    if (!actionConfig?.fields) return true;

    const errors: Record<string, string> = {};
    let isValid = true;

    actionConfig.fields.forEach(field => {
      const value = state.fieldValues[field.fieldName];
      const error = validateField(field, value);
      if (error) {
        errors[field.fieldName] = error;
        isValid = false;
      }
    });

    setState(prev => ({ ...prev, fieldErrors: errors }));
    return isValid;
  }, [actionConfig, state.fieldValues, validateField]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!actionConfig || !artifact) return;
    
    // Check if this is a simple action (no API call, just trigger action)
    const simpleAction = (actionConfig as any).simpleAction;
    if (simpleAction) {
      setState(prev => ({ ...prev, isSubmitting: true, submitError: null }));
      try {
        // First trigger the specific action for scene progress tracking
        onAction?.(simpleAction.action, {
          ...simpleAction.payload,
          artifact: artifact
        });
        
        // Then trigger action-completed for scene advancement if needed
        if (simpleAction.advanceScene) {
          onAction?.('action-completed', {
            ...simpleAction.payload,
            artifact: artifact,
            advanceScene: true
          });
        }
        
        setState(prev => ({
          ...prev,
          isSubmitting: false,
          submitSuccess: true,
          submitError: null
        }));
        return;
      } catch (error: any) {
        setState(prev => ({
          ...prev,
          isSubmitting: false,
          submitError: error.message || 'Action failed'
        }));
        return;
      }
    }
    
    // Validate all fields for regular API call actions
    if (!validateAllFields()) {
      return;
    }

    setState(prev => ({ ...prev, isSubmitting: true, submitError: null }));

    try {
      // Prepare API call
      const apiConfig = actionConfig.apiCall;
      if (!apiConfig) {
        throw new Error('No API configuration provided');
      }

      // Replace {id} in endpoint with artifact.id
      const endpoint = apiConfig.endpoint.replace('{id}', artifact.id);

      // Build request body with only specified fields
      const requestBody: Record<string, any> = {};
      apiConfig.fieldsToUpdate.forEach(fieldName => {
        if (state.fieldValues[fieldName] !== undefined) {
          requestBody[fieldName] = state.fieldValues[fieldName];
        }
      });

      // Make API call
      let response;
      switch (apiConfig.method) {
        case 'PATCH':
          response = await api.patch(endpoint, requestBody);
          break;
        case 'PUT':
          response = await api.put(endpoint, requestBody);
          break;
        case 'POST':
          response = await api.post(endpoint, requestBody);
          break;
        default:
          throw new Error(`Unsupported method: ${apiConfig.method}`);
      }

      // Success
      setState(prev => ({
        ...prev,
        isSubmitting: false,
        submitSuccess: true,
        submitError: null
      }));

      // Call success handler
      if (actionConfig.onSuccess) {
        actionConfig.onSuccess(response.data);
      }

      // Trigger card action for parent components
      onAction?.('action-completed', {
        artifact: artifact,
        updates: requestBody,
        response: response.data,
        advanceScene: apiConfig.advanceScene
      });

      // Also trigger context panel update if artifact was modified
      onAction?.('show-context', {
        type: 'artifact',
        artifact: { ...artifact, ...requestBody }
      });

    } catch (error: any) {
      console.error('Action submission failed:', error);
      const errorMessage = actionConfig.apiCall?.errorMessage || 
        error.response?.data?.detail || 
        error.message ||
        'Failed to submit action';

      setState(prev => ({
        ...prev,
        isSubmitting: false,
        submitSuccess: false,
        submitError: errorMessage
      }));

      if (actionConfig.onError) {
        actionConfig.onError(error);
      }
    }
  }, [actionConfig, artifact, state.fieldValues, validateAllFields, onAction]);

  // Early return after all hooks
  if (!actionConfig || !artifact) {
    return null;
  }

  // Render field based on type
  const renderField = (field: FieldConfig) => {
    const value = state.fieldValues[field.fieldName] ?? '';
    const error = state.fieldErrors[field.fieldName];
    const hasError = !!error;

    const baseInputStyle: React.CSSProperties = {
      width: '100%',
      padding: '10px 12px',
      fontSize: '14px',
      border: hasError ? '2px solid #ef4444' : '1px solid #d1d5db',
      borderRadius: '6px',
      outline: 'none',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit'
    };

    const focusStyle = {
      borderColor: hasError ? '#ef4444' : '#3b82f6',
      boxShadow: hasError ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : '0 0 0 3px rgba(59, 130, 246, 0.1)'
    };

    switch (field.fieldType) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            placeholder={field.placeholder}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value, field)}
            style={baseInputStyle}
            onFocus={(e) => Object.assign(e.target.style, focusStyle)}
            onBlur={(e) => {
              e.target.style.borderColor = hasError ? '#ef4444' : '#d1d5db';
              e.target.style.boxShadow = 'none';
            }}
            disabled={state.isSubmitting}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            placeholder={field.placeholder}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value, field)}
            style={baseInputStyle}
            onFocus={(e) => Object.assign(e.target.style, focusStyle)}
            onBlur={(e) => {
              e.target.style.borderColor = hasError ? '#ef4444' : '#d1d5db';
              e.target.style.boxShadow = 'none';
            }}
            disabled={state.isSubmitting}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value, field)}
            style={baseInputStyle}
            onFocus={(e) => Object.assign(e.target.style, focusStyle)}
            onBlur={(e) => {
              e.target.style.borderColor = hasError ? '#ef4444' : '#d1d5db';
              e.target.style.boxShadow = 'none';
            }}
            disabled={state.isSubmitting}
          />
        );

      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value, field)}
            style={{
              ...baseInputStyle,
              cursor: 'pointer'
            }}
            onFocus={(e) => Object.assign(e.target.style, focusStyle)}
            onBlur={(e) => {
              e.target.style.borderColor = hasError ? '#ef4444' : '#d1d5db';
              e.target.style.boxShadow = 'none';
            }}
            disabled={state.isSubmitting}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            placeholder={field.placeholder}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value, field)}
            rows={field.rows || 4}
            style={{
              ...baseInputStyle,
              resize: 'vertical',
              minHeight: '80px'
            }}
            onFocus={(e) => Object.assign(e.target.style, focusStyle)}
            onBlur={(e) => {
              e.target.style.borderColor = hasError ? '#ef4444' : '#d1d5db';
              e.target.style.boxShadow = 'none';
            }}
            disabled={state.isSubmitting}
          />
        );

      case 'checkbox':
        return (
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: state.isSubmitting ? 'not-allowed' : 'pointer'
          }}>
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleFieldChange(field.fieldName, e.target.checked, field)}
              style={{
                width: '18px',
                height: '18px',
                cursor: state.isSubmitting ? 'not-allowed' : 'pointer'
              }}
              disabled={state.isSubmitting}
            />
            <span style={{ fontSize: '14px', color: '#374151' }}>
              {field.label}
            </span>
          </label>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      padding: '16px',
      backgroundColor: '#f0f9ff',
      border: '2px solid #3b82f6',
      borderRadius: '8px',
      margin: '12px 0'
    }}>
      {/* Title */}
      {actionConfig.title && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '16px'
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            backgroundColor: '#3b82f6',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            !
          </div>
          <h4 style={{
            margin: 0,
            color: '#1e40af',
            fontSize: '15px',
            fontWeight: '600'
          }}>
            {actionConfig.title}
          </h4>
        </div>
      )}

      {/* Description */}
      {actionConfig.description && (
        <p style={{
          margin: '0 0 16px 0',
          color: '#1e40af',
          fontSize: '13px',
          lineHeight: '1.5'
        }}>
          {actionConfig.description}
        </p>
      )}

      {/* Status Display */}
      {actionConfig.showStatus && artifact?.id && (
        <ArtifactStatusDisplay
          artifactId={artifact.id}
          source={actionConfig.statusSource || 'artifact-item'}
        />
      )}

      {/* Workflow Steps */}
      {actionConfig.workflowSteps && actionConfig.workflowSteps.length > 0 && (
        <WorkflowStepsDisplay
          artifactId={artifact?.id || ''}
          steps={actionConfig.workflowSteps}
          onStepAction={(step) => {
            // Trigger action for workflow step
            if (step.actionEndpoint) {
              onAction?.('workflow-step', { step, artifact });
            }
          }}
        />
      )}

      {/* Fields */}
      {actionConfig.fields?.map((field, index) => (
        <div key={field.fieldName} style={{ marginBottom: '16px' }}>
          {/* Label (skip for checkbox, it's inline) */}
          {field.fieldType !== 'checkbox' && (
            <label style={{
              display: 'block',
              marginBottom: '6px',
              fontSize: '13px',
              fontWeight: '500',
              color: '#374151'
            }}>
              {field.label}
              {field.required && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
            </label>
          )}

          {/* Field Input */}
          {renderField(field)}

          {/* Helper Text */}
          {field.helperText && !state.fieldErrors[field.fieldName] && (
            <p style={{
              margin: '4px 0 0 0',
              fontSize: '12px',
              color: '#6b7280',
              fontStyle: 'italic'
            }}>
              {field.helperText}
            </p>
          )}

          {/* Error Message */}
          {state.fieldErrors[field.fieldName] && (
            <p style={{
              margin: '4px 0 0 0',
              fontSize: '12px',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <ExclamationCircleOutlined />
              {state.fieldErrors[field.fieldName]}
            </p>
          )}
        </div>
      ))}

      {/* Submit Button */}
      {actionConfig.submitButton && (
        <button
          onClick={handleSubmit}
          disabled={state.isSubmitting || state.submitSuccess}
          style={{
            width: '100%',
            padding: '12px 24px',
            backgroundColor: state.submitSuccess ? '#10b981' : (actionConfig.submitButton.color || '#3b82f6'),
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: state.isSubmitting || state.submitSuccess ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            opacity: state.isSubmitting ? 0.7 : 1
          }}
          onMouseEnter={(e) => {
            if (!state.isSubmitting && !state.submitSuccess) {
              e.currentTarget.style.backgroundColor = '#2563eb';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.3)';
            }
          }}
          onMouseLeave={(e) => {
            if (!state.submitSuccess) {
              e.currentTarget.style.backgroundColor = actionConfig.submitButton?.color || '#3b82f6';
            }
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {state.isSubmitting && (
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid white',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 0.6s linear infinite'
            }} />
          )}
          {state.submitSuccess && <CheckCircleOutlined />}
          <span>
            {state.isSubmitting ? 'Submitting...' : 
             state.submitSuccess ? 'Success!' :
             actionConfig.submitButton.label}
          </span>
        </button>
      )}

      {/* Success Message */}
      {state.submitSuccess && actionConfig.apiCall?.successMessage && (
        <div style={{
          marginTop: '12px',
          padding: '12px',
          backgroundColor: '#d1fae5',
          border: '1px solid #10b981',
          borderRadius: '6px',
          color: '#065f46',
          fontSize: '13px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <CheckCircleOutlined style={{ fontSize: '16px' }} />
          {actionConfig.apiCall.successMessage}
        </div>
      )}

      {/* Error Message */}
      {state.submitError && (
        <div style={{
          marginTop: '12px',
          padding: '12px',
          backgroundColor: '#fee2e2',
          border: '1px solid #ef4444',
          borderRadius: '6px',
          color: '#991b1b',
          fontSize: '13px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <ExclamationCircleOutlined style={{ fontSize: '16px' }} />
          {state.submitError}
        </div>
      )}

      {/* Add spinning animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

